/**
 * Combines active-subscription and free-access-whitelist status into a single
 * "does this user have premium access" signal. `loading` only reflects the
 * subscription lookup (the whitelist listener has no separate loading state),
 * matching how the two were previously combined ad hoc at each call site.
 *
 * Both signals are checked across the whole linked family (parent + child),
 * not just the current account — so a whitelisted parent (not a paying
 * subscriber) also unlocks premium for their linked child, the same way an
 * active subscription already does.
 */
import { useMemo } from 'react';
import { useHasActiveSubscription } from './useSubscription';
import { useIsAnyEmailWhitelisted } from './useFreeAccessWhitelist';
import { useLinkedIdentity } from './useLinkedIdentity';

export function useIsPremium(email: string | null): {
  isPremium: boolean;
  loading: boolean;
} {
  const { hasActiveSubscription, loading } = useHasActiveSubscription();
  const { linkedEmails } = useLinkedIdentity();
  const emailsToCheck = useMemo(
    () => [...(email ? [email] : []), ...(linkedEmails ?? [])],
    [email, linkedEmails],
  );
  const isWhitelisted = useIsAnyEmailWhitelisted(emailsToCheck);
  return { isPremium: hasActiveSubscription || isWhitelisted, loading };
}
